import React, { useRef, useState, useEffect, useContext } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "@mantine/core";
import { ArrowRight } from "lucide-react";
import "./upload-requisition-form.css";
import { Check, X } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { GlobalContext } from "../../GlobalContext";
import ProviderConsentForm from "../consent-forms/provider-consent-form";

const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === 'string') {
    const cleanAmount = amount.replace(/[₦,]/g, '').replace(/\.00$/, '');
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const UploadRequisitionForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const test_slug = searchParams.get("test_slug"); // Extract test_slug from query parameters
  const order_id = searchParams.get("order_id"); // Extract order_id from query parameters
  const { userData: globalUserData } = useContext(GlobalContext);

  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("initial");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [testData, setTestData] = useState({
    testName: "No order selected",
    testPrice: "No order selected",
  });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState("individual");
  const [orderData, setOrderData] = useState(null);
  // Provider-specific states
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [consentModalOpened, setConsentModalOpened] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const [consentModalMode, setConsentModalMode] = useState("new");
  const [existingConsentData, setExistingConsentData] = useState(null);
  const [patientData, setPatientData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: { month: "", date: "", year: "" },
    gender: "",
    homeAddress: "",
  });
  const [patientErrors, setPatientErrors] = useState({});
  const [patientTouched, setPatientTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    month: false,
    date: false,
    year: false,
    gender: false,
    homeAddress: false,
  });
  // User data for individual role
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: { month: "", date: "", year: "" },
    gender: "",
    homeAddress: "",
  });

  const uploadInterval = useRef(null);
  const abortController = useRef(null);
  const isCanceled = useRef(false);
  const searchRef = useRef(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/orders";
  const UPDATE_ORDER_API_ENDPOINT = order_id ? `/wp-json/dbsb/v1/orders/${order_id}/update` : null;
  const ORDER_DETAILS_API_ENDPOINT = order_id ? `/wp-json/dbsb/v1/orders/${order_id}` : null;
  const TEST_API_ENDPOINT = "/wp-json/dbsb/v1/medical-test";
  const USER_API_ENDPOINT = "/wp-json/dbsb/v1/user/me";
  const PATIENTS_API_ENDPOINT = "/wp-json/dbsb/v1/user/patients";
  const PATIENT_API_ENDPOINT = "/wp-json/dbsb/v1/patients";
  const CONSENT_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/submit";
  const CONSENT_CHECK_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/check/provider";
  const CONSENT_GET_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/user/provider";
  const CONSENT_UPDATE_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms";

  const springProgress = useSpring(0, { stiffness: 100, damping: 20 });
  const displayProgress = useTransform(
    springProgress,
    (value) => `${Math.round(value)}%`
  );

  useEffect(() => {
    // Map global context role to form role
    const roleFromContext =
      globalUserData.role === "patient" ? "individual" : "provider";
    setUserRole(roleFromContext);

    const fetchUserDetails = async () => {
      try {
        const response = await fetch(USER_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings?.nonce || "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user details");
        }

        const userData = await response.json();
        setCurrentUserId(userData.id);
        // Parse date_of_birth, support DD/MM/YYYY and YYYY-MM-DD
        let dob = { month: "", date: "", year: "" };
        if (userData.date_of_birth && typeof userData.date_of_birth === "string") {
          const dobStr = userData.date_of_birth.trim();
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) {
            const [day, month, year] = dobStr.split("/");
            dob = {
              month: parseInt(month, 10) || "",
              date: day || "",
              year: year || "",
            };
          } else if (/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
            const [year, month, day] = dobStr.split("-");
            dob = {
              month: parseInt(month, 10) || "",
              date: day || "",
              year: year || "",
            };
          }
        }
        setUserData({
          firstName: userData.first_name || "",
          lastName: userData.last_name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          dateOfBirth: dob,
          gender: userData.gender || "",
          homeAddress: "", // Initialize as blank, to be filled by user
        });
      } catch (error) {
        console.error("Error fetching user details:", error);
        setError("Unable to authenticate user. Please log in and try again.");
      }
    };

    fetchUserDetails();
  }, [globalUserData.role]);

  useEffect(() => {
    const fetchTestDetails = async () => {
      // If order_id is provided but test_slug is not, fetch order details first
      if (order_id && !test_slug && ORDER_DETAILS_API_ENDPOINT) {
        try {
          if (!window.wpApiSettings?.nonce) {
            throw new Error(
              "Authentication token not found. Please try logging in again."
            );
          }

          const orderResponse = await fetch(ORDER_DETAILS_API_ENDPOINT, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          });

          if (!orderResponse.ok) {
            throw new Error("Failed to fetch order details.");
          }

          const orderData = await orderResponse.json();
          const orderTestSlug = orderData.test?.slug;

          if (orderTestSlug) {
            // Fetch test details using the slug from order
            const testResponse = await fetch(
              `${TEST_API_ENDPOINT}?test_slug=${orderTestSlug}`,
              {
                method: "GET",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  "X-WP-Nonce": window.wpApiSettings.nonce,
                },
              }
            );

            if (!testResponse.ok) {
              throw new Error("Failed to fetch test details.");
            }

            const testData = await testResponse.json();
            setTestData({
              testName: testData.title || "No order selected",
              testPrice: testData.test_price
                ? `₦${testData.test_price}`
                : "No order selected",
            });
            return;
          } else {
            throw new Error("Test slug not found in order details.");
          }
        } catch (error) {
          console.error("Error fetching order/test details:", error);
          setTestData({
            testName: "No order selected",
            testPrice: "No order selected",
          });
          setError(error.message || "Failed to fetch test details.");
          return;
        }
      }

      if (!test_slug) {
        setTestData({
          testName: "No order selected",
          testPrice: "No order selected",
        });
        return;
      }

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }
        const response = await fetch(
          `${TEST_API_ENDPOINT}?test_slug=${test_slug}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Test not found.");
          } else if (response.status === 401) {
            throw new Error("You must be logged in to view test details.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view this test.");
          }
          throw new Error("Failed to fetch test details.");
        }

        const data = await response.json();
        setTestData({
          testName: data.title || "No order selected",
          testPrice: data.test_price
            ? `₦${data.test_price}`
            : "No order selected",
        });
      } catch (error) {
        console.error("Error fetching test details:", error);
        setTestData({
          testName: "No order selected",
          testPrice: "No order selected",
        });
        setError(error.message || "Failed to fetch test details.");
      }
    };

    fetchTestDetails();
  }, [test_slug, order_id]);

  useEffect(() => {
    if (userRole === "provider") {
      const fetchPatients = async () => {
        try {
          const response = await fetch(PATIENTS_API_ENDPOINT, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings?.nonce || "",
            },
          });
          if (!response.ok) throw new Error("Failed to fetch patients");
          const data = await response.json();
          setPatients(data.patients || []);
          setFilteredPatients(data.patients || []);
        } catch (error) {
          console.error("Error fetching patients:", error);
        }
      };
      fetchPatients();
    }
  }, [userRole]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle 3-second auto-hide for general error message
  useEffect(() => {
    if (showGeneralError) {
      const timer = setTimeout(() => {
        setShowGeneralError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showGeneralError]);

  // Validate form when data changes (like patient requisition form)
  useEffect(() => {
    if (userRole === "individual") {
      validateUser(hasAttemptedSubmit);
    } else {
      validatePatient(hasAttemptedSubmit);
    }
  }, [patientData, userData, patientTouched, hasAttemptedSubmit, userRole]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    setSelectedPatient(null);
    setPatientData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: { month: "", date: "", year: "" },
      gender: "",
      homeAddress: "",
    });
    setPatientTouched({
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
      month: false,
      date: false,
      year: false,
      gender: false,
      homeAddress: false,
    });

    if (query) {
      const filtered = patients.filter((patient) =>
        `${patient.first_name} ${patient.last_name}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setFilteredPatients(filtered);
      setShowDropdown(true);
    } else {
      setFilteredPatients(patients);
      setShowDropdown(true);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchQuery(`${patient.first_name} ${patient.last_name}`);
    setShowDropdown(false);

    const convertDateFormat = (dateString) => {
      if (!dateString || typeof dateString !== "string") return null;
      const regex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!regex.test(dateString)) return null;

      const [day, month, year] = dateString.split("/");
      return `${year}-${month}-${day}`;
    };

    const isValidDate = (dateString) => {
      const date = new Date(dateString);
      return !isNaN(date.getTime());
    };

    let dob = { month: "", date: "", year: "" };
    const convertedDate = convertDateFormat(patient.date_of_birth);

    if (convertedDate && isValidDate(convertedDate)) {
      const parsedDate = new Date(convertedDate);
      dob = {
        month: String(parsedDate.getMonth() + 1),
        date: String(parsedDate.getDate()),
        year: String(parsedDate.getFullYear()),
      };
    } else {
      console.warn(
        `Invalid date_of_birth for patient ${patient.id}:`,
        patient.date_of_birth
      );
    }

    setPatientData({
      firstName: patient.first_name || "",
      lastName: patient.last_name || "",
      email: patient.email || "",
      phone: patient.phone || "",
      dateOfBirth: dob,
      gender: patient.gender || "",
      homeAddress: "", // Initialize as blank, to be filled by user
    });
    setPatientTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      month: true,
      date: true,
      year: true,
      gender: true,
      homeAddress: false, // Set to false to prevent initial error
    });
    // Do not call validatePatient immediately
  };

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    updatePatientForm(name, value);
    
    // Validate immediately if field has been touched or if we've attempted submit
    if (hasAttemptedSubmit || patientTouched[name]) {
      setPatientTouched((prev) => ({ ...prev, [name]: true }));
      // Clear error immediately if value becomes valid
      if (name === "homeAddress") {
        const newErrors = { ...patientErrors };
        if (value.trim()) {
          delete newErrors.homeAddress;
        } else if (hasAttemptedSubmit || patientTouched[name]) {
          newErrors.homeAddress = "Home Address is required";
        }
        setPatientErrors(newErrors);
      } else {
        // For other fields, use the existing validation logic
        validatePatient(hasAttemptedSubmit);
      }
    }
  };

  const handlePatientBlur = (e) => {
    const { name } = e.target;
    setPatientTouched((prev) => {
      const updated = { ...prev, [name]: true };
      return updated;
    });
    // Validate immediately after updating touched state
    // The validation will use the updated state on next render
    setTimeout(() => {
      validatePatient(true); // Always validate on blur
    }, 0);
  };

  const handlePatientPhoneChange = (value) => {
    updatePatientForm("phone", value || "");
    if (hasAttemptedSubmit) {
      setPatientTouched((prev) => ({ ...prev, phone: true }));
      validatePatient(true);
    }
  };

  const handlePatientPhoneBlur = () => {
    setPatientTouched((prev) => ({ ...prev, phone: true }));
    validatePatient(hasAttemptedSubmit);
  };

  const handlePatientDateChange = (field, value) => {
    let sanitizedValue = value;
    if (field === "date") {
      sanitizedValue = value.slice(0, 2);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else if (field === "year") {
      sanitizedValue = value.slice(0, 4);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else {
      updatePatientForm("dateOfBirth", {
        ...patientData.dateOfBirth,
        [field]: sanitizedValue,
      });
    }
    if (hasAttemptedSubmit) {
      setPatientTouched((prev) => ({ ...prev, [field]: true }));
      validatePatient(true);
    }
  };

  const handlePatientDateBlur = (field) => {
    setPatientTouched((prev) => ({ ...prev, [field]: true }));
    validatePatient(hasAttemptedSubmit);
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
    
    // Validate immediately if field has been touched or if we've attempted submit
    if (hasAttemptedSubmit || patientTouched[name]) {
      setPatientTouched((prev) => ({ ...prev, [name]: true }));
      // Clear error immediately if value becomes valid
      if (name === "homeAddress") {
        const newErrors = { ...patientErrors };
        if (value.trim()) {
          delete newErrors.homeAddress;
        } else if (hasAttemptedSubmit || patientTouched[name]) {
          newErrors.homeAddress = "Home Address is required";
        }
        setPatientErrors(newErrors);
      }
    }
  };

  const handleUserBlur = (e) => {
    const { name } = e.target;
    setPatientTouched((prev) => {
      const updated = { ...prev, [name]: true };
      return updated;
    });
    // Validate immediately after updating touched state
    // The validation will use the updated state on next render
    setTimeout(() => {
      validateUser(true); // Always validate on blur
    }, 0);
  };

  const updatePatientForm = (name, value) => {
    if (name === "dateOfBirth") {
      setPatientData((prev) => ({
        ...prev,
        dateOfBirth: value,
      }));
    } else {
      setPatientData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validatePatient = (shouldValidateAll = false) => {
    const newErrors = {};
    const touched = patientTouched;
    const currentYear = new Date().getFullYear();

    if ((shouldValidateAll || touched.firstName) && !patientData.firstName)
      newErrors.firstName = "First Name is required";
    if ((shouldValidateAll || touched.lastName) && !patientData.lastName)
      newErrors.lastName = "Last Name is required";
    if (shouldValidateAll || touched.email) {
      if (!patientData.email) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(patientData.email))
        newErrors.email = "Please enter a valid email address";
    }
    if (shouldValidateAll || touched.phone) {
      if (!patientData.phone) newErrors.phone = "Phone number is required";
      else {
        const phoneNum = patientData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15)
          newErrors.phone = "Please enter a valid phone number";
      }
    }
    if ((shouldValidateAll || touched.gender) && !patientData.gender) 
      newErrors.gender = "Gender is required";
    
    const { month, date, year } = patientData.dateOfBirth;
    if ((shouldValidateAll || touched.month) && !month) 
      newErrors.month = "Month is required";
    if (shouldValidateAll || touched.date) {
      if (!date) newErrors.date = "Day is required";
      else if (isNaN(Number(date)) || Number(date) < 1 || Number(date) > 31)
        newErrors.date = "Please enter a valid day (1-31)";
    }
    if (shouldValidateAll || touched.year) {
      if (!year) newErrors.year = "Year is required";
      else if (year.length === 4) {
        const yearNum = Number(year);
        if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear)
          newErrors.year = "Please enter a valid year";
      } else {
        newErrors.year = "Please enter a valid 4-digit year";
      }
    }
    // Validate homeAddress only if touched
    if ((shouldValidateAll || touched.homeAddress) && !patientData.homeAddress)
      newErrors.homeAddress = "Home Address is required";

    setPatientErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const validateUser = (shouldValidateAll = false) => {
    const newErrors = {};
    const touched = patientTouched;
    if ((shouldValidateAll || touched.homeAddress) && (!userData.homeAddress || !userData.homeAddress.trim()))
      newErrors.homeAddress = "Home Address is required";
    setPatientErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const isPatientIncomplete = () => {
    const { dateOfBirth, homeAddress, ...otherFields } = patientData;
    return (
      (!selectedPatient &&
        (Object.values(otherFields).some((value) => value === "") ||
          Object.values(dateOfBirth).some((value) => value === ""))) ||
      !homeAddress // Always check homeAddress
    );
  };

  const isUserIncomplete = () => {
    // Check if homeAddress is empty (required field)
    return !userData.homeAddress || !userData.homeAddress.trim();
  };

  // Check if form has errors or is incomplete
  const isFormInvalid = () => {
    // Check if consent form is submitted
    if (!consentSubmitted) return true;
    
    // Check if file is uploaded
    if (uploadStatus !== "complete" || !file) return true;
    
    // Check if there are any patient errors
    const hasPatientErrors = Object.keys(patientErrors).length > 0 && 
      Object.values(patientErrors).some((error) => error !== "" && error !== undefined);
    
    // Check if patient information is incomplete (for provider role)
    if (userRole === "provider") {
      if (!selectedPatient && isPatientIncomplete()) return true;
      if (hasPatientErrors) return true;
      // Check if homeAddress is empty (required field)
      if (!patientData.homeAddress || !patientData.homeAddress.trim()) return true;
    }
    
    // Check if user information is incomplete (for individual role)
    if (userRole === "individual") {
      if (isUserIncomplete()) return true;
      if (hasPatientErrors) return true;
    }
    
    return false;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validFormats = ["application/pdf"];
      const maxSize = 10 * 1024 * 1024;

      if (!validFormats.includes(selectedFile.type)) {
        setError("Only PDF files are supported");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      if (selectedFile.size > maxSize) {
        setError("File size exceeds 10MB limit");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      setFile(selectedFile);
      setUploadStatus("uploading");
      setError("");
      setSuccess("");
      setIsUploading(true);
      isCanceled.current = false;

      let currentProgress = 0;

      const animateProgress = () => {
        if (currentProgress < 90) {
          currentProgress = Math.min(currentProgress + 2, 90);
          setProgress(currentProgress);
          springProgress.set(currentProgress);
          uploadInterval.current = setTimeout(animateProgress, 100);
        }
      };

      animateProgress();

      try {
        // Simulate upload without actual API call
        await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate delay

        if (!isCanceled.current) {
          clearTimeout(uploadInterval.current);
          uploadInterval.current = null;
          setProgress(100);
          springProgress.set(100);
          setUploadStatus("complete");
          setSuccess("Requisition form selected successfully!");
        }
      } catch (error) {
        if (!isCanceled.current) {
          clearTimeout(uploadInterval.current);
          uploadInterval.current = null;
          setUploadStatus("failed");
          setError("Failed to process file, please try again.");
          setProgress(0);
          springProgress.set(0);
        }
      } finally {
        if (!isCanceled.current) {
          setIsUploading(false);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileEvent = { target: { files: [droppedFile] } };
      handleFileChange(fileEvent);
    }
  };

  const handleUploadAnother = () => {
    setFile(null);
    setUploadStatus("initial");
    setError("");
    setSuccess("");
    setProgress(0);
    springProgress.set(0);
    setIsUploading(false);
    setOrderData(null);
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }
    abortController.current = null;
  };

  const handleCancelUpload = () => {
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }

    isCanceled.current = true;

    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }

    setUploadStatus("initial");
    setFile(null);
    setProgress(0);
    springProgress.set(0);
    setError("");
    setSuccess("");
    setIsUploading(false);
    setOrderData(null);
  };

  // Check for existing consent form
  useEffect(() => {
    const checkExistingConsent = async () => {
      try {
        if (!window.wpApiSettings?.nonce) {
          return;
        }

        const response = await fetch(CONSENT_CHECK_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.has_consent) {
            const consentResponse = await fetch(CONSENT_GET_API_ENDPOINT, {
              method: "GET",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": window.wpApiSettings.nonce,
              },
            });

            if (consentResponse.ok) {
              const consentData = await consentResponse.json();
              setExistingConsentData(consentData.consent_form);
              setConsentSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error("Error checking existing consent:", error);
      }
    };

    checkExistingConsent();
  }, []);

  const handleConsentSubmit = async (consentData) => {
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const isUpdate = consentData.id;
      const endpoint = isUpdate ? `${CONSENT_UPDATE_API_ENDPOINT}/${consentData.id}` : CONSENT_API_ENDPOINT;

      const response = await fetch(endpoint, {
        method: isUpdate ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        body: JSON.stringify({
          user_type: "provider",
          ...consentData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit consent form");
      }

      const result = await response.json();
      setConsentSubmitted(true);
      setExistingConsentData(result.consent_form || consentData);
      setConsentModalOpened(false);
      setConsentModalMode("new");
    } catch (error) {
      console.error("Error submitting consent:", error);
      throw error;
    }
  };

  const handleEditConsent = () => {
    setConsentModalMode("edit");
    setConsentModalOpened(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    setSubmissionSuccess(false);

    setPatientTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      month: true,
      date: true,
      year: true,
      gender: true,
      homeAddress: true,
    });

    // Check if consent form is submitted
    if (!consentSubmitted) {
      setError("You must complete the consent form");
      setShowGeneralError(true);
      return;
    }

    // Check if file is uploaded first
    if (uploadStatus !== "complete" || !file) {
      setError("Please upload a valid requisition form");
      setShowGeneralError(true);
      return;
    }

    // Validate form fields
    if (userRole === "individual" && !validateUser(true)) {
      setError("Please upload a valid requisition form");
      setShowGeneralError(true);
      return;
    }

    if (userRole === "provider" && !validatePatient(true)) {
      setError("Please upload a valid requisition form");
      setShowGeneralError(true);
      return;
    }

    if (
      userRole === "provider" &&
      !selectedPatient &&
      !patientData.homeAddress
    ) {
      setError("Please upload a valid requisition form");
      setShowGeneralError(true);
      return;
    }

    if (userRole === "provider" && !selectedPatient) {
      const dobFormatted = `${patientData.dateOfBirth.year}-${String(
        patientData.dateOfBirth.month
      ).padStart(2, "0")}-${String(patientData.dateOfBirth.date).padStart(
        2,
        "0"
      )}`;

      try {
        const patientResponse = await fetch(PATIENT_API_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          credentials: "same-origin",
          body: JSON.stringify({
            first_name: patientData.firstName,
            last_name: patientData.lastName,
            email: patientData.email,
            phone: patientData.phone,
            gender: patientData.gender,
            dob: dobFormatted,
            address: patientData.homeAddress,
          }),
        });

        const patientDataResponse = await patientResponse.json();
        if (!patientResponse.ok) {
          throw new Error(
            patientDataResponse.message || "Failed to add patient."
          );
        }

        setSelectedPatient({
          id: patientDataResponse.patient_id,
          ...patientData,
        });
      } catch (error) {
        setError(error.message || "Failed to add patient. Please try again.");
        return;
      }
    }

    if (uploadStatus === "complete" && file) {
      setIsSubmitting(true); // Set submitting state
      setError("");
      setSuccess("");
      abortController.current = new AbortController();

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        // If order_id exists, update the order; otherwise create a new one (for backward compatibility)
        if (order_id && UPDATE_ORDER_API_ENDPOINT) {
          // Update existing order
          const formData = new FormData();
          formData.append("requisition_pdf", file);
          formData.append("collection_method", "lab_visit");
          formData.append("requisition_type", "pdf");

          const response = await fetch(UPDATE_ORDER_API_ENDPOINT, {
            method: "PUT",
            body: formData,
            credentials: "include",
            headers: {
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
            signal: abortController.current.signal,
          });

          const data = await response.json();

          if (!response.ok) {
            let errorMessage = "Upload failed";
            if (response.status === 401) {
              errorMessage = "You must be logged in to upload a document.";
            } else if (response.status === 403) {
              errorMessage = "You don't have permission to upload a document.";
            } else if (response.status === 404) {
              errorMessage = "Order not found.";
            } else if (data.code === "missing_pdf") {
              errorMessage = "PDF file is required for PDF submission.";
            } else if (data.message) {
              errorMessage = data.message;
            }
            throw new Error(errorMessage);
          }

          setSubmissionSuccess(true);
          setTimeout(() => {
            navigate(`/my-order/${order_id}`, {
              state: { refresh: true, timestamp: Date.now() },
            });
          }, 3000);
        } else {
          // Create new order (backward compatibility)
          const formData = new FormData();
          formData.append("requisition_pdf", file);
          formData.append("order_type", userRole);
          formData.append("test_slug", test_slug || "");
          if (userRole === "provider" && selectedPatient) {
            formData.append("health_provider_user_id", currentUserId || "");
            formData.append("patient_post_id", selectedPatient.id || "");
          } else if (userRole === "individual") {
            formData.append("patient_user_id", currentUserId || "");
          }
          formData.append("requisition_type", "pdf");
          formData.append("order_date", new Date().toISOString().split("T")[0]);
          formData.append("collection_method", "lab_visit");

          const response = await fetch(API_ENDPOINT, {
            method: "POST",
            body: formData,
            credentials: "include",
            headers: {
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
            signal: abortController.current.signal,
          });

          const data = await response.json();

          if (!response.ok) {
            let errorMessage = "Upload failed";
            if (response.status === 401) {
              errorMessage = "You must be logged in to upload a document.";
            } else if (response.status === 403) {
              errorMessage = "You don't have permission to upload a document.";
            } else if (data.code === "missing_pdf") {
              errorMessage = "PDF file is required for PDF submission.";
            } else if (data.code === "invalid_test") {
              errorMessage = "The specified test does not exist.";
            } else if (data.message) {
              errorMessage = data.message;
            }
            throw new Error(errorMessage);
          }

          setOrderData({
            id: data.id,
            order_id: data.order_id,
            test: {
              name: testData.testName,
              price: isNaN(Number(testData.testPrice))
                ? testData.testPrice
                : formatPrice(testData.testPrice),
            },
            total_amount: isNaN(Number(testData.testPrice))
              ? testData.testPrice
              : formatPrice(testData.testPrice),
            payment_link: data.payment_link || null,
            order_status: data.order_status,
            payment_status: data.payment_status,
            collection: {
              method: data.collection_method,
              date: data.collection_date,
            },
          });

          const submissionData = userRole === "provider" ? patientData : userData;
          const dobFormatted = `${submissionData.dateOfBirth.year}-${String(
            submissionData.dateOfBirth.month
          ).padStart(2, "0")}-${String(submissionData.dateOfBirth.date).padStart(
            2,
            "0"
          )}`;

          const requisitionFormData = {
            firstName: submissionData.firstName,
            lastName: submissionData.lastName,
            email: submissionData.email,
            phone: submissionData.phone,
            dateOfBirth: dobFormatted,
            gender: submissionData.gender,
            address: submissionData.homeAddress,
          };

          const updatedOrderData = {
            ...data,
            requisition_form_data: JSON.stringify(requisitionFormData),
            test: {
              name: testData.testName,
              price: isNaN(Number(testData.testPrice))
                ? testData.testPrice
                : formatPrice(testData.testPrice),
            },
            total_amount: isNaN(Number(testData.testPrice))
              ? testData.testPrice
              : formatPrice(testData.testPrice),
          };

          setSubmissionSuccess(true);
          setTimeout(() => {
            navigate(
              `/review-test-order?order_id=${data.order_id}&test_slug=${data.test_slug}`
            );
          }, 3000);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setError(
            error.message || "Failed to create order. Please try again."
          );
          setUploadStatus("failed");
          setProgress(0);
          springProgress.set(0);
        }
      } finally {
        setIsSubmitting(false); // Reset submitting state
        setIsUploading(false);
      }
    } else {
      setError("Please upload a valid requisition form.");
    }
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const uploadAreaVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    uploading: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    complete: {
      opacity: 1,
      scale: 1.02,
      backgroundImage:
        "linear-gradient(260.26deg, rgba(47, 64, 211, 0.07) 12.78%, rgba(214, 215, 225, 0.07) 118.25%)",
      transition: { duration: 0.4, ease: "easeOut" },
    },
    failed: {
      opacity: 1,
      scale: 0.98,
      transition: { duration: 0.4, ease: "easeIn" },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
    disabled: { scale: 1, transition: { duration: 0.1 } },
  };

  return (
    <>
      <motion.div
        className="upload-requisition-form-card"
        variants={itemVariants}
      >
        <motion.form
          onSubmit={handleSubmit}
          className="upload-requisition-form"
          variants={formVariants}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {userRole === "provider" && (
            <div className="provider-specific-info">
              <motion.div variants={itemVariants} className="form-group">
                <h3>Search Patient</h3>
                <div className="search-container" ref={searchRef}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search for a patient..."
                    className="search-input"
                  />
                  {showDropdown && (
                    <div className="dropdown">
                      {filteredPatients.length > 0 ? (
                        filteredPatients.map((patient) => (
                          <div
                            key={patient.id}
                            className="dropdown-item"
                            onClick={() => handlePatientSelect(patient)}
                          >
                            {patient.first_name} {patient.last_name}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item no-patient-dropdown">
                          No patients found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>

              {!selectedPatient && (
                <motion.div variants={itemVariants} className="divider">
                  <span>OR</span>
                </motion.div>
              )}

              <motion.div variants={itemVariants} className="form-group">
                <h3>
                  {selectedPatient ? "Patient Information" : "Add Patient"}
                </h3>
                <div className="form-group name-row">
                  <div className="name-input">
                    <label>First Name</label>
                    <input
                      name="firstName"
                      value={patientData.firstName}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter first name"
                      disabled={!!selectedPatient}
                    />
                    {patientErrors.firstName && !selectedPatient && (
                      <span className="error">{patientErrors.firstName}</span>
                    )}
                  </div>
                  <div className="name-input">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={patientData.lastName}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter last name"
                      disabled={!!selectedPatient}
                    />
                    {patientErrors.lastName && !selectedPatient && (
                      <span className="error">{patientErrors.lastName}</span>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={patientData.email}
                    onChange={handlePatientChange}
                    onBlur={handlePatientBlur}
                    placeholder="Enter email address"
                    disabled={!!selectedPatient}
                  />
                  {patientErrors.email && !selectedPatient && (
                    <span className="error">{patientErrors.email}</span>
                  )}
                </div>
                <div className="form-group phone">
                  <label>Phone Number</label>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="NG"
                    value={patientData.phone}
                    onChange={handlePatientPhoneChange}
                    onBlur={handlePatientPhoneBlur}
                    placeholder="Enter phone number"
                    maxLength={17}
                    disabled={!!selectedPatient}
                  />
                  {patientErrors.phone && !selectedPatient && (
                    <span className="error">{patientErrors.phone}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={patientData.gender}
                    onChange={handlePatientChange}
                    onBlur={handlePatientBlur}
                    disabled={!!selectedPatient}
                  >
                    <option value="">Select from dropdown</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {patientErrors.gender && !selectedPatient && (
                    <span className="error">{patientErrors.gender}</span>
                  )}
                </div>
                <div className="form-group dob">
                  <label>Date of Birth</label>
                  <div className="dob-inputs">
                    <div className="dob-input">
                      <select
                        name="month"
                        value={patientData.dateOfBirth.month}
                        onChange={(e) =>
                          handlePatientDateChange("month", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("month")}
                        disabled={!!selectedPatient}
                      >
                        <option value="">Month</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(0, i).toLocaleString("default", {
                              month: "long",
                            })}
                          </option>
                        ))}
                      </select>
                      {patientErrors.month && !selectedPatient && (
                        <span className="error">{patientErrors.month}</span>
                      )}
                    </div>
                    <div className="dob-input">
                      <input
                        type="text"
                        name="date"
                        value={patientData.dateOfBirth.date}
                        onChange={(e) =>
                          handlePatientDateChange("date", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("date")}
                        placeholder="Day"
                        maxLength="2"
                        disabled={!!selectedPatient}
                        style={{
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                      />
                      {patientErrors.date && !selectedPatient && (
                        <span className="error">{patientErrors.date}</span>
                      )}
                    </div>
                    <div className="dob-input">
                      <input
                        type="text"
                        name="year"
                        value={patientData.dateOfBirth.year}
                        onChange={(e) =>
                          handlePatientDateChange("year", e.target.value)
                        }
                        onBlur={() => handlePatientDateBlur("year")}
                        placeholder="Year"
                        maxLength="4"
                        disabled={!!selectedPatient}
                        style={{
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                      />
                      {patientErrors.year && !selectedPatient && (
                        <span className="error">{patientErrors.year}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Home Address</label>
                  <textarea
                    name="homeAddress"
                    value={patientData.homeAddress}
                    onChange={handlePatientChange}
                    onBlur={handlePatientBlur}
                    placeholder="Enter address"
                    rows="4"
                  />
                  {patientErrors.homeAddress && (
                    <span className="error">{patientErrors.homeAddress}</span>
                  )}
                </div>
              </motion.div>
            </div>
          )}

          {userRole === "individual" && (
            <motion.div variants={itemVariants} className="form-group">
              <h3>Patient Information</h3>
              <div className="form-group name-row">
                <div className="name-input">
                  <label>First Name</label>
                  <input name="firstName" value={userData.firstName} disabled />
                </div>
                <div className="name-input">
                  <label>Last Name</label>
                  <input name="lastName" value={userData.lastName} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={userData.email}
                  disabled
                />
              </div>
              <div className="form-group phone">
                <label>Phone Number</label>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="NG"
                  value={userData.phone}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select name="gender" value={userData.gender} disabled>
                  <option value="">Select from dropdown</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group dob">
                <label>Date of Birth</label>
                <div className="dob-inputs">
                  <div className="dob-input">
                    <select
                      name="month"
                      value={userData.dateOfBirth.month}
                      disabled
                    >
                      <option value="">Month</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="dob-input">
                    <input
                      type="text"
                      name="date"
                      value={userData.dateOfBirth.date}
                      placeholder="Day"
                      maxLength="2"
                      disabled
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                      }}
                    />
                  </div>
                  <div className="dob-input">
                    <input
                      type="text"
                      name="year"
                      value={userData.dateOfBirth.year}
                      placeholder="Year"
                      maxLength="4"
                      disabled
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Home Address</label>
                <textarea
                  name="homeAddress"
                  value={userData.homeAddress}
                  onChange={handleUserChange}
                  onBlur={handleUserBlur}
                  placeholder="Enter address"
                  rows="4"
                />
                {patientErrors.homeAddress && (
                  <span className="error">{patientErrors.homeAddress}</span>
                )}
              </div>
            </motion.div>
          )}

          <motion.h2 variants={itemVariants}>Upload requisition form</motion.h2>

          <motion.div
            key={uploadStatus}
            className={`upload-area ${
              uploadStatus === "complete" ? "upload-complete" : ""
            }`}
            variants={uploadAreaVariants}
            initial="hidden"
            animate={
              uploadStatus === "uploading"
                ? "uploading"
                : uploadStatus === "complete"
                ? "complete"
                : uploadStatus === "failed"
                ? "failed"
                : "visible"
            }
          >
            {uploadStatus === "initial" && (
              <>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/folder-open.svg`}
                  className="file-icon"
                  alt="Folder Open"
                />
                <p>Drag & drop your file here</p>
                <div className="or-line">
                  <ul></ul>
                  <p>OR</p>
                  <ul></ul>
                </div>
                <label className="browse-button">
                  Browse files
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf"
                    style={{ display: "none" }}
                    disabled={isUploading}
                  />
                </label>
              </>
            )}
            {uploadStatus === "uploading" && (
              <>
                <motion.div
                  className="progress-bar"
                  style={{ width: displayProgress }}
                />
                <img
                  src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                  className="file-icon"
                  alt="Folder Close"
                />
                <p>{file?.name}</p>
                <div className="upload-status">
                  <span>{Math.round(progress)}%</span>
                  <span>Uploading</span>
                  <span className="dot"></span>
                  <span className="file-size">
                    {((file?.size * progress) / 100 / 1024 / 1024).toFixed(2)}{" "}
                    MB
                  </span>
                </div>
                <button
                  className="cancel-button"
                  onClick={handleCancelUpload}
                  disabled={!isUploading}
                >
                  Cancel
                </button>
              </>
            )}
            {uploadStatus === "complete" && (
              <>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                  className="file-icon"
                  alt="Folder Close"
                />
                <p>{file?.name}</p>
                <p className="upload-status-message-success">
                  <Check size={16} className="status-icon" /> Upload complete
                </p>
                <button
                  className="upload-another-button"
                  onClick={handleUploadAnother}
                >
                  Upload another file
                </button>
              </>
            )}
            {uploadStatus === "failed" && (
              <>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                  className="file-icon"
                  alt="Folder Close"
                />
                <p>{file?.name}</p>
                <p className="upload-status-message-failed">
                  <X size={16} className="status-icon" /> {error}
                </p>
                <button
                  className="upload-another-button"
                  onClick={handleUploadAnother}
                >
                  Upload another file
                </button>
              </>
            )}
          </motion.div>
          {error && uploadStatus !== "failed" && (
            <span className="error">{error}</span>
          )}
          <div className="file-types-container">
            <div className="file-type-box">PDF</div>
            <div className="file-type-box">&lt; 10MB</div>
          </div>

          {/* Consent Form Section */}
          <motion.div variants={itemVariants} className="form-group">
            {!consentSubmitted ? (
              <div className="consent-button-container">
                <button
                  type="button"
                  onClick={() => {
                    setConsentModalMode("new");
                    setConsentModalOpened(true);
                  }}
                  className="fill-consent-button"
                >
                  <span>Fill the consent form</span>
                  <ArrowRight size={20} />
                </button>
                {hasAttemptedSubmit && !consentSubmitted && (
                  <span className="error">You must complete the consent form</span>
                )}
              </div>
            ) : (
              <div className="consent-acknowledgment">
                <p>
                  By continuing, you agree and acknowledge to Syndicate Bio's{" "}
                  <span
                    onClick={handleEditConsent}
                    className="consent-link"
                  >
                    Consent Form.
                  </span>
                </p>
              </div>
            )}
          </motion.div>
        </motion.form>
      </motion.div>

      {/* Consent Form Modal */}
      <Modal
        opened={consentModalOpened}
        onClose={() => {
          setConsentModalOpened(false);
          setConsentModalMode("new");
        }}
        withCloseButton={false}
        centered
        size="lg"
        className="consent-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <ProviderConsentForm
          onSubmit={handleConsentSubmit}
          isEditMode={consentModalMode === "edit"}
          existingData={existingConsentData}
        />
      </Modal>
      {showGeneralError && (
        <motion.div 
          className="general-error-message"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          Fill required fields and fill in the correct manner
        </motion.div>
      )}
      <motion.div
        variants={itemVariants}
        className="button-group upload-button-group"
      >
        {submissionSuccess ? (
          <p className="submission-success-message">
            Form Submitted Successfully.
          </p>
        ) : (
          <motion.button
            type="submit"
            onClick={handleSubmit}
            className={`continue-button ${
              isUploading || isSubmitting || isFormInvalid() ? "disabled" : ""
            }`}
            disabled={isUploading || isSubmitting || isFormInvalid()}
            variants={buttonVariants}
            whileHover="hover"
            whileTap={
              uploadStatus === "complete" && !isSubmitting && !isFormInvalid()
                ? "tap"
                : "disabled"
            }
          >
            {isSubmitting ? "Submitting..." : "Submit Form"}
          </motion.button>
        )}
      </motion.div>
    </>
  );
};

export default UploadRequisitionForm;
